import { t } from "ttag";

import {
  getMedicationRequestStatusLabel,
  MEDICATION_REQUEST_STATUS_COLOR,
} from "@/features/my-meds/models";
import type { PastMedicationRequest } from "@/features/my-meds/models";
import { Badge, EmptyState, IconWrapper, Table, Tooltip } from "@/shared/ui";
import { tw } from "@/shared/utils";
import { ResolutionIcon } from "./resolution-icon";

interface PastMedicationRequestsTableProps {
  pastMedicationRequests: PastMedicationRequest[];
}

export const PastMedicationRequestsTable = ({
  pastMedicationRequests,
}: PastMedicationRequestsTableProps) => {
  return (
    <Table.Root>
      <Table.Header>
        <Table.Row>
          <Table.Head>{t`Drug name`}</Table.Head>
          <Table.Head>{t`Dose`}</Table.Head>
          <Table.Head>{t`Supply`}</Table.Head>
          <Table.Head>{t`Date`}</Table.Head>
          <Table.Head className="text-center">{t`Status`}</Table.Head>
          <Table.Head className="truncate text-right">{t`Tracking #`}</Table.Head>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {pastMedicationRequests.length ? (
          pastMedicationRequests.map(
            ({
              dose,
              dateOfRequest,
              status,
              trackingNumber,
              resolution,
              supply,
              product,
              drugName,
            }) => (
              <Table.Row key={`past-medication-request-${trackingNumber}`}>
                <Table.Cell>{product?.name ?? drugName ?? "-"}</Table.Cell>
                <Table.Cell>{dose ?? "-"}</Table.Cell>
                <Table.Cell>{supply ? `${supply} month(s)` : "-"}</Table.Cell>
                <Table.Cell>{dateOfRequest}</Table.Cell>
                <Table.Cell>
                  <div className="flex justify-center">
                    <Tooltip.Provider>
                      <Tooltip.Root>
                        <Tooltip.Trigger
                          className={tw(!resolution && "cursor-default")}
                        >
                          <Badge
                            color={MEDICATION_REQUEST_STATUS_COLOR[status]}
                            size="x-small"
                            className="min-w-24"
                          >
                            {getMedicationRequestStatusLabel(status)}
                          </Badge>
                        </Tooltip.Trigger>

                        {resolution && (
                          <Tooltip.Content className="flex gap-1">
                            <IconWrapper size="sm">
                              <ResolutionIcon resolution={resolution} />
                            </IconWrapper>
                            {resolution}
                          </Tooltip.Content>
                        )}
                      </Tooltip.Root>
                    </Tooltip.Provider>
                  </div>
                </Table.Cell>
                <Table.Cell className="text-right">{trackingNumber}</Table.Cell>
              </Table.Row>
            ),
          )
        ) : (
          <Table.Row>
            <Table.Cell colSpan={6}>
              <EmptyState.Root className="border-none">
                <EmptyState.Icon />
                <EmptyState.Description>
                  {t`No past medication requests yet`}
                </EmptyState.Description>
              </EmptyState.Root>
            </Table.Cell>
          </Table.Row>
        )}
      </Table.Body>
    </Table.Root>
  );
};
